/* eslint-disable jsx-a11y/anchor-is-valid */
import {useCallback, useEffect, useReducer, useRef, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'

import {signin, adminMfaEnrollStart, adminMfaEnrollConfirm, adminMfaVerify} from '../../../config/api'
import {describeApiFailure, initialLoginFlowState, loginFlowReducer} from '../mfa/loginFlow'
import {MfaCodeStep} from './MfaCodeStep'
import {MfaEnrollStep} from './MfaEnrollStep'
import {BackupCodesStep} from './BackupCodesStep'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  email: '',
  password: '',
}

/** Where the panel keeps the admin session token (read by AppRoutes / config/api.js getToken). */
const SESSION_STORAGE_KEY = 'lava20_admin'

/**
 * Persist the session token and enter the panel — identical to the pre-TOTP behaviour.
 * On the enrollment path this runs only after the user acknowledged the backup codes.
 */
function commitSession(token: string) {
  localStorage.setItem(SESSION_STORAGE_KEY, token)
  window.location.href = '/dashboard'
}

/*
  Two-step login state machine (spec §6):
    password → code         → done
    password → enroll       → backup-codes → done
    password → done         (ADMIN_TOTP_ENFORCED=false: legacy {status, token})
  All transitions live in ../mfa/loginFlow.ts (pure, unit-tested); this component only
  wires the API calls and renders the step.
*/
export function Login() {
  const [flow, dispatch] = useReducer(loginFlowReducer, initialLoginFlowState)
  const [busy, setBusy] = useState(false)
  const [enrollLoading, setEnrollLoading] = useState(false)
  const committedRef = useRef(false)
  /** mfa_token for which POST /mfa/enroll/start has already been fired (avoids double start). */
  const enrollStartedForRef = useRef<string | null>(null)

  const fail = useCallback((err: unknown) => {
    dispatch({type: 'REQUEST_FAILED', failure: describeApiFailure(err)})
  }, [])

  // ---- Step 1: email + password -------------------------------------------------
  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: (values, {setSubmitting}) => {
      setBusy(true)
      dispatch({type: 'CLEAR_ERROR'})
      signin({email: values.email.trim(), password: values.password}).then(
        (res) => {
          dispatch({type: 'LOGIN_SUCCEEDED', response: res || {}})
          setBusy(false)
          setSubmitting(false)
        },
        (err) => {
          fail(err)
          setBusy(false)
          setSubmitting(false)
        }
      )
    },
  })

  // ---- Commit the session exactly once when the machine reaches "done" ----------
  useEffect(() => {
    if (flow.step === 'done' && flow.sessionToken && !committedRef.current) {
      committedRef.current = true
      commitSession(flow.sessionToken)
    }
  }, [flow.step, flow.sessionToken])

  // ---- Enrollment: fetch the QR as soon as we enter the enroll step --------------
  const startEnrollment = useCallback(
    (mfaToken: string) => {
      enrollStartedForRef.current = mfaToken
      setEnrollLoading(true)
      dispatch({type: 'CLEAR_ERROR'})
      adminMfaEnrollStart(mfaToken).then(
        (res) => {
          dispatch({type: 'ENROLL_STARTED', response: res || {}})
          setEnrollLoading(false)
        },
        (err) => {
          fail(err)
          setEnrollLoading(false)
        }
      )
    },
    [fail]
  )

  useEffect(() => {
    if (
      flow.step === 'enroll' &&
      flow.mfaToken &&
      !flow.enrollment &&
      enrollStartedForRef.current !== flow.mfaToken
    ) {
      startEnrollment(flow.mfaToken)
    }
    if (flow.step !== 'enroll') {
      enrollStartedForRef.current = null
    }
  }, [flow.step, flow.mfaToken, flow.enrollment, startEnrollment])

  const handleRetryEnrollStart = () => {
    if (flow.mfaToken && !enrollLoading) startEnrollment(flow.mfaToken)
  }

  const handleEnrollConfirm = (code: string) => {
    if (!flow.mfaToken || busy) return
    setBusy(true)
    dispatch({type: 'CLEAR_ERROR'})
    adminMfaEnrollConfirm(flow.mfaToken, code).then(
      (res) => {
        dispatch({type: 'ENROLL_CONFIRMED', response: res || {}})
        setBusy(false)
      },
      (err) => {
        fail(err)
        setBusy(false)
      }
    )
  }

  // ---- Verification (TOTP or backup code) --------------------------------------
  const handleVerify = (code: string) => {
    if (!flow.mfaToken || busy) return
    setBusy(true)
    dispatch({type: 'CLEAR_ERROR'})
    adminMfaVerify(flow.mfaToken, code).then(
      (res) => {
        dispatch({type: 'VERIFIED', response: res || {}})
        setBusy(false)
      },
      (err) => {
        fail(err)
        setBusy(false)
      }
    )
  }

  const handleBackToPassword = () => {
    if (busy) return
    dispatch({type: 'BACK_TO_PASSWORD'})
    formik.resetForm({values: {email: formik.values.email, password: ''}})
  }

  // ---- Render -------------------------------------------------------------------
  if (flow.step === 'code') {
    return (
      <MfaCodeStep
        submitting={busy}
        error={flow.error}
        errorSeq={flow.errorSeq}
        onSubmitTotp={handleVerify}
        onSubmitBackupCode={handleVerify}
        onBack={handleBackToPassword}
      />
    )
  }

  if (flow.step === 'enroll') {
    return (
      <MfaEnrollStep
        enrollment={flow.enrollment}
        loadingEnrollment={enrollLoading}
        submitting={busy}
        error={flow.error}
        errorSeq={flow.errorSeq}
        onSubmitCode={handleEnrollConfirm}
        onRetryStart={handleRetryEnrollStart}
        onBack={handleBackToPassword}
      />
    )
  }

  if (flow.step === 'backup-codes') {
    return (
      <BackupCodesStep
        codes={flow.backupCodes}
        email={formik.values.email}
        onContinue={() => dispatch({type: 'CODES_ACKNOWLEDGED'})}
      />
    )
  }

  if (flow.step === 'done') {
    return (
      <div className='text-center py-20'>
        <span className='spinner-border spinner-border-lg text-primary mb-5' role='status' aria-hidden='true'></span>
        <div className='text-gray-500 fw-semibold fs-6'>Signing you in...</div>
      </div>
    )
  }

  return (
    <form className='form w-100' onSubmit={formik.handleSubmit} noValidate id='kt_login_signin_form'>
      {/* begin::Heading */}
      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>Sign In</h1>
      </div>
      {/* begin::Heading */}

      {flow.error && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{flow.error} </div>
        </div>
      )}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
        <input
          placeholder='Email'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.email && formik.errors.email},
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
          type='email'
          name='email'
          autoComplete='off'
          disabled={busy}
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.email}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-3'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0'>Password</label>
        <input
          type='password'
          autoComplete='off'
          {...formik.getFieldProps('password')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': formik.touched.password && formik.errors.password,
            },
            {
              'is-valid': formik.touched.password && !formik.errors.password,
            }
          )}
          disabled={busy}
        />
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Action */}
      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || !formik.isValid || busy}
        >
          {!busy && <span className='indicator-label'>Continue</span>}
          {busy && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}
    </form>
  )
}
